import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip, Switch, FormControlLabel,
  Divider,
} from "@mui/material";
import { Plus, Pencil, X, Building, Mail, Phone, MapPin, Globe, Settings as SettingsIcon, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import { adminGetSettings, adminUpdateSetting, adminCreateSetting } from "../../api/settings";

// Setting categories for grouping
const SETTING_CATEGORIES = {
  COMPANY: ["company_name", "company_tagline"],
  CONTACT: ["email", "main_phone", "whatsapp_number", "address"],
  SITE: ["footer_text"],
};

const CATEGORY_ICONS = {
  COMPANY: Building,
  CONTACT: Phone,
  SITE: Globe,
};

const CATEGORY_LABELS = {
  COMPANY: "Company Information",
  CONTACT: "Contact Details",
  SITE: "Site Settings",
};

const CATEGORY_COLORS = {
  COMPANY: "#2563EB",
  CONTACT: "#16A34A",
  SITE: "#B45309",
};

// Get category for a setting key
const getSettingCategory = (key) => {
  for (const [category, keys] of Object.entries(SETTING_CATEGORIES)) {
    if (keys.includes(key)) return category;
  }
  return "SITE";
};

// Field type configurations
const FIELD_TYPES = {
  company_name: { type: "text", label: "Company Name", icon: Building, placeholder: "Enter company name" },
  company_tagline: { type: "text", label: "Company Tagline", icon: Building, placeholder: "Enter company tagline" },
  email: { type: "email", label: "Email", icon: Mail, placeholder: "contact@example.com" },
  main_phone: { type: "tel", label: "Main Phone", icon: Phone, placeholder: "+91 98765 43210" },
  whatsapp_number: { type: "tel", label: "WhatsApp Number", icon: Phone, placeholder: "+91 98765 43210" },
  address: { type: "text", label: "Address", icon: MapPin, placeholder: "Enter company address", multiline: true },
  footer_text: { type: "text", label: "Footer Text", icon: Globe, placeholder: "Copyright text", multiline: true },
};

export default function SettingsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => adminGetSettings().then(r => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, data }) => adminUpdateSetting(key, data),
    onSuccess: () => { 
      qc.invalidateQueries(["admin-settings"]); 
      notify("Setting saved"); 
      closeDialog(); 
    },
    onError: (e) => notify(e?.response?.data?.error || "Failed to save setting", "error"),
  });

  const createMutation = useMutation({
    mutationFn: adminCreateSetting,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-settings"]); 
      notify("Setting created"); 
      closeDialog(); 
    },
    onError: (e) => notify(e?.response?.data?.error || "Failed to create setting", "error"),
  });

  const saving = updateMutation.isPending || createMutation.isPending;

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { key: "", value: "", is_public: true },
  });

  const onSubmit = (values) => {
    if (editingKey) {
      updateMutation.mutate({ key: editingKey, data: { key: editingKey, value: values.value, is_public: values.is_public } });
    } else {
      createMutation.mutate({ key: values.key, value: values.value, is_public: values.is_public });
    }
  };

  const openCreate = () => {
    setEditingKey(null);
    reset({ key: "", value: "", is_public: true });
    setOpen(true);
  };

  const openEdit = (setting) => {
    setEditingKey(setting.key);
    reset({
      key: setting.key,
      value: setting.value || "",
      is_public: setting.is_public ?? true,
    });
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditingKey(null);
    reset({ key: "", value: "", is_public: true });
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);

  // Group settings by category
  const groupedSettings = Object.entries(SETTING_CATEGORIES).map(([category, keys]) => {
    const categorySettings = settings.filter(s => keys.includes(s.key));
    return { category, keys, settings: categorySettings };
  }).filter(g => g.settings.length > 0);

  // Also include uncategorized settings
  const uncategorizedKeys = settings.filter(s => 
    !Object.values(SETTING_CATEGORIES).flat().includes(s.key)
  );
  if (uncategorizedKeys.length > 0) {
    groupedSettings.push({ category: "OTHER", keys: uncategorizedKeys.map(s => s.key), settings: uncategorizedKeys });
  }

  // Filter by search
  const filteredGroups = groupedSettings.map(group => ({
    ...group,
    settings: group.settings.filter(s => 
      s.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.value?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(g => g.settings.length > 0);

  return (
    <AdminShell title="Settings">
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Configure site-wide settings. Changes will be reflected on the public site immediately.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" } }}
        >
          Add Setting
        </Button>
      </Box>

      {/* Search bar */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search settings by key or value..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{ sx: { fontSize: 13.5 } }}
          />
        </Box>
      </Card>

      {/* Settings grouped by category */}
      {isLoading ? (
        <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", p: 2 }}>
                <Skeleton variant="text" width={150} />
                <Box sx={{ mt: 1 }}>
                  <Skeleton variant="text" width="100%" />
                </Box>
              </Card>
            ))}
          </Box>
        </Card>
      ) : filteredGroups.length === 0 ? (
        <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 8, textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
            <SettingsIcon size={22} color="#2563EB" />
          </Box>
          <Typography fontWeight={700} color="#0F172A" mb={0.5}>
            {searchQuery ? "No matching settings found" : "No settings yet"}
          </Typography>
          <Typography color="text.secondary" fontSize={13.5}>
            {searchQuery ? "Try adjusting your search." : "Add settings to configure your site."}
          </Typography>
        </Card>
      ) : (
        filteredGroups.map((group) => {
          const Icon = CATEGORY_ICONS[group.category] || SettingsIcon;
          const color = CATEGORY_COLORS[group.category] || "#2563EB";
          const label = CATEGORY_LABELS[group.category] || group.category;
          
          return (
            <Card key={group.category} sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
              {/* Category header */}
              <Box sx={{ 
                background: `linear-gradient(135deg, ${color}1A 0%, ${color}26 100%)`,
                px: 2.5, py: 2, 
                display: "flex", 
                alignItems: "center", 
                gap: 1
              }}>
                <Icon size={18} color={color} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                  {label}
                </Typography>
              </Box>
              
              {/* Settings in this category */}
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {group.settings.map((setting) => {
                  const fieldConfig = FIELD_TYPES[setting.key] || { type: "text", label: setting.key, icon: SettingsIcon };
                  const FieldIcon = fieldConfig.icon;
                  
                  return (
                    <Card key={setting.key} sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ 
                          width: 36, 
                          height: 36, 
                          borderRadius: "9px", 
                          bgcolor: "rgba(37,99,235,0.08)", 
                          color: "#2563EB", 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          flexShrink: 0 
                        }}>
                          <FieldIcon size={16} strokeWidth={2.1} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#0F172A", mb: 0.5 }}>
                            {fieldConfig.label || setting.key}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontFamily: "monospace", mb: 1 }}>
                            {setting.key}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 13.5, color: "text.secondary", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {setting.value || "—"}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(setting)}
                                sx={{ 
                                  border: "1px solid rgba(15,23,42,0.1)", 
                                  borderRadius: "8px", 
                                  "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                                }}
                              >
                                <Pencil size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Chip
                          label={setting.is_public ? "Public" : "Private"}
                          size="small"
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: 11, 
                            height: 20,
                            bgcolor: setting.is_public ? "rgba(34,197,94,0.08)" : "rgba(107,114,128,0.08)",
                            color: setting.is_public ? "#16A34A" : "#6B7280"
                          }}
                        />
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Card>
          );
        })
      )}

      {/* Create / Edit dialog */}
      <Dialog 
        open={open} 
        onClose={closeDialog} 
        maxWidth="sm" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
      >
        <Box sx={{ 
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          px: 3, py: 2.5,
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>
              {editingKey ? "Edit Setting" : "New Setting"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              {editingKey ? `Update the value for "${editingKey}".` : "Add a new site setting."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
            <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                <SettingsIcon size={16} color="#2563EB" />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Setting Details
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                <Controller
                  name="key"
                  control={control}
                  rules={{ 
                    required: "Key is required",
                    pattern: { value: /^[a-z_]+$/, message: "Key must be lowercase letters and underscores only" }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Setting Key"
                      fullWidth
                      disabled={saving || editingKey}
                      error={!!fieldState.error}
                      helperText={editingKey ? "Key cannot be changed" : fieldState.error?.message}
                      placeholder="e.g. company_name, email"
                    />
                  )}
                />
                <Controller
                  name="value"
                  control={control}
                  rules={{ required: "Value is required" }}
                  render={({ field, fieldState }) => {
                    const fieldConfig = editingKey ? FIELD_TYPES[editingKey] : FIELD_TYPES.company_name;
                    const FieldIcon = fieldConfig.icon;
                    return (
                      <TextField
                        {...field}
                        label="Value"
                        fullWidth
                        disabled={saving}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder={fieldConfig.placeholder}
                        type={fieldConfig.type}
                        multiline={fieldConfig.multiline}
                        rows={fieldConfig.multiline ? 3 : 1}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><FieldIcon size={15} color="#94A3B8" /></InputAdornment>
                        }}
                      />
                    );
                  }}
                />
                <Controller
                  name="is_public"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} disabled={saving} color="primary" />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Globe size={16} color={field.value ? "#16A34A" : "#6B7280"} />
                          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                            {field.value ? "Public (visible on site)" : "Private (admin only)"}
                          </Typography>
                        </Box>
                      }
                      disabled={saving}
                    />
                  )}
                />
              </Box>
            </Card>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit" 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", px: 2.5, minWidth: 100, "&:hover": { bgcolor: "#1D4ED8" } }}
            >
              {saving ? "Saving..." : editingKey ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}